import {
  createHttpKitchenApiClient,
  type HttpTransport,
  type HttpTransportRequest,
  type HttpTransportResponse,
  type KitchenApiClient,
} from '@kitchen-inventory/api-client';
import type { AuthSession } from '@kitchen-inventory/shared-types';

import { currentAccessToken, sessionStore, storedRefreshToken } from '../auth/session.store.js';
import { appConfig } from '../config/app-config.js';

/**
 * Transporte con renovación automática de sesión.
 *
 * Ante un 401 pide un access token nuevo con el refresh y reintenta **una sola
 * vez**. Las renovaciones concurrentes comparten la misma promesa para no
 * disparar N refresh si varias consultas caducan a la vez.
 */
let refreshInFlight: Promise<boolean> | undefined;

async function rawFetch(request: HttpTransportRequest): Promise<HttpTransportResponse> {
  const response = await fetch(`${request.baseUrl}${request.path}`, {
    method: request.method,
    headers: request.headers,
    ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
  });

  const text = await response.text();
  const data: unknown = text.length === 0 ? undefined : safeParse(text);

  if (!response.ok) {
    throw { status: response.status, data };
  }
  return { status: response.status, data };
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isUnauthorized(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { status?: unknown }).status === 401
  );
}

async function renewSession(): Promise<boolean> {
  const refreshToken = storedRefreshToken();
  if (refreshToken === undefined) {
    return false;
  }
  try {
    const response = await rawFetch({
      baseUrl: appConfig.apiUrl,
      method: 'POST',
      path: '/auth/refresh',
      body: { refreshToken },
      headers: { 'Content-Type': 'application/json' },
    });
    const session = unwrap<AuthSession>(response.data);
    if (session === undefined) {
      return false;
    }
    sessionStore.getState().setSession(session);
    return true;
  } catch {
    return false;
  }
}

function unwrap<T>(payload: unknown): T | undefined {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }
  const envelope = payload as { data?: unknown };
  return (envelope.data ?? payload) as T;
}

const transport: HttpTransport = async (request) => {
  try {
    return await rawFetch(request);
  } catch (error: unknown) {
    if (!isUnauthorized(error) || request.path === '/auth/refresh') {
      throw error;
    }

    refreshInFlight ??= renewSession().finally(() => {
      refreshInFlight = undefined;
    });
    const renewed = await refreshInFlight;

    if (!renewed) {
      sessionStore.getState().clear();
      throw error;
    }

    const token = currentAccessToken();
    return rawFetch({
      ...request,
      headers: {
        ...request.headers,
        ...(token === undefined ? {} : { Authorization: `Bearer ${token}` }),
      },
    });
  }
};

export const apiClient: KitchenApiClient = createHttpKitchenApiClient({
  baseUrl: appConfig.apiUrl,
  tokenProvider: currentAccessToken,
  transport,
});

/**
 * Descarga binaria autenticada.
 *
 * Los PDF no pasan por el cliente generado porque este espera JSON. Comparte el
 * mismo token y la misma renovación: un enlace directo al endpoint no llevaría
 * la cabecera de autorización y devolvería 401.
 */
export async function downloadBlob(path: string): Promise<{ blob: Blob; fileName: string }> {
  const pedir = async (): Promise<Response> => {
    const token = currentAccessToken();
    return fetch(`${appConfig.apiUrl}${path}`, {
      headers: token === undefined ? {} : { Authorization: `Bearer ${token}` },
    });
  };

  let response = await pedir();
  if (response.status === 401) {
    refreshInFlight ??= renewSession().finally(() => {
      refreshInFlight = undefined;
    });
    if (await refreshInFlight) {
      response = await pedir();
    }
  }

  if (!response.ok) {
    throw { status: response.status, data: undefined };
  }

  return { blob: await response.blob(), fileName: fileNameOf(response, path) };
}

/** El nombre lo manda el servidor en Content-Disposition; si no, se deduce. */
function fileNameOf(response: Response, path: string): string {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const encontrado = /filename="([^"]+)"/.exec(disposition)?.[1];
  return encontrado ?? `${path.split('/').filter(Boolean).join('-')}.pdf`;
}
